import { useCallback, useEffect, useMemo, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { plaidService } from "../../services/plaidService";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertCircle,
  CheckCircle2,
  Landmark,
  Link2,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type { Account } from "../../interface/Account";

type PanelStatus =
  | "loading-token"
  | "ready"
  | "linking"
  | "exchanging"
  | "success"
  | "unavailable"
  | "error";

interface PlaidLinkPanelProps {
  open: boolean;
  userId?: string;
  onAccountsAdd: (accounts: Account[]) => void;
  onClose: () => void;
}

/**
 * Launches Plaid Link and imports the linked accounts into the shared
 * Account/Position model. Falls back gracefully when the backend Plaid
 * contract is not yet available.
 */
export function PlaidLinkPanel({ open, userId, onAccountsAdd, onClose }: PlaidLinkPanelProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [status, setStatus] = useState<PanelStatus>("loading-token");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState(0);

  // Fetch a fresh link token whenever the panel is opened.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setStatus("loading-token");
    setErrorMessage(null);
    setImportedCount(0);
    setLinkToken(null);

    if (!userId) {
      setStatus("error");
      setErrorMessage("You must be signed in to link an account.");
      return;
    }

    plaidService
      .createLinkToken(userId)
      .then((res: { linkToken?: string } | undefined) => {
        if (cancelled) return;
        const token = res?.linkToken;
        if (!token) {
          setStatus("unavailable");
          setErrorMessage("Bank linking is not enabled on this environment yet.");
          return;
        }
        setLinkToken(token);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("unavailable");
        setErrorMessage(
          "Bank linking isn't available yet. Reach out to admin (info@wealthbrainai.com) to enable it for you. You can still add accounts manually or via CSV."
        );
      });

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const handleSuccess = useCallback(
    async (publicToken: string, metadata: any) => {
      setStatus("exchanging");
      setErrorMessage(null);
      try {
        const institutionId = metadata?.institution?.institution_id;
        const institutionName = metadata?.institution?.name;
        const result: { accounts?: Account[] } | undefined =
          await plaidService.exchangePublicToken(userId!, publicToken, {
            institutionId,
            institutionName,
          });
        const accounts = result?.accounts ?? [];
        setImportedCount(accounts.length);
        setStatus("success");
        if (accounts.length > 0) {
          onAccountsAdd(accounts);
        }
      } catch {
        setStatus("error");
        setErrorMessage("We couldn't import your accounts. Please try again.");
      }
    },
    [userId, onAccountsAdd]
  );

  const handleExit = useCallback(() => {
    setStatus((prev) => (prev === "linking" ? "ready" : prev));
  }, []);

  const { open: openPlaid, ready: plaidReady, error: plaidError } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
  });

  useEffect(() => {
    if (plaidError) {
      setStatus("error");
      setErrorMessage("Plaid Link failed to initialize. Please try again.");
    }
  }, [plaidError]);

  const canLink = useMemo(
    () => status === "ready" && plaidReady && !!linkToken,
    [status, plaidReady, linkToken]
  );

  const startLink = () => {
    setStatus("linking");
    setErrorMessage(null);
    setTimeout(() => openPlaid(), 150);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-muted/40 p-5">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Landmark className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-medium flex items-center gap-2">
              Link a financial institution
              <Badge variant="secondary" className="text-[10px]">
                Plaid
              </Badge>
            </h3>
            <p className="text-sm text-muted-foreground">
              Securely connect your bank, brokerage, or retirement accounts. We use Plaid
              to aggregate balances, holdings, and transactions — automatically.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button onClick={startLink} disabled={!canLink}>
            {status === "loading-token" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Preparing…
              </>
            ) : status === "exchanging" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing accounts…
              </>
            ) : status === "linking" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Opening Plaid…
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Connect a bank account
              </>
            )}
          </Button>

          {status === "success" && (
            <span className="text-sm text-green-700 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              {importedCount > 0
                ? `Imported ${importedCount} account${importedCount === 1 ? "" : "s"}`
                : "Linked successfully"}
            </span>
          )}
        </div>

        {status === "unavailable" && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {status === "error" && errorMessage && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {status === "success" && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Accounts are syncing in the background. Holdings and transactions update
              automatically (typically daily) and can be refreshed on demand.
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-4">
        <span className="flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          Your credentials are never shared with us.
        </span>
        {status === "success" && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Done
          </Button>
        )}
      </div>
    </div>
  );
}

export default PlaidLinkPanel;
